import React from 'react'
import {observer} from 'mobx-react-lite'
import {AgGridReactProps} from 'ag-grid-react'
import {Grid, Paper, Typography} from '@mui/material'
import {makeOcStyles} from '@oc/styles'
import {OcAgGrid, getOcAgGridClassName} from '../AgGrid/OcAgGrid'
import {OcButton} from '../BasicOcComponents/OcButton'
import {OcTextField} from '../BasicOcComponents/OcTextFields'
import {SearchIcon} from '@oc/icons'
import './ocTreeStyles.css'
import {OcTreeStateBase} from './OcTreeState'
import {OcTreeItemBasicProps} from './OcTreeUtil'
import {debounce} from 'lodash'

interface TreeHeaderProps<T extends OcTreeItemBasicProps<T>> {
    title: string
    model: OcTreeStateBase<T>
    hideHeaderActions?: boolean
    action?: JSX.Element
    expandText?: string
    collapseText?: string
    searchText?: string
}

export interface OcTreeProps<T extends OcTreeItemBasicProps<T>> extends TreeHeaderProps<T>, AgGridReactProps {}
export const OcTreeClassNamePrefix = 'oc-tree-component'
export const OcTreeClassName = getOcAgGridClassName(OcTreeClassNamePrefix)

const headerHeight = 60
const useStyles = makeOcStyles(theme => {
    return {
        displayFlex: {
            display: 'flex'
        },
        justifyEnd: {
            justifyContent: 'end'
        },
        justifyCenter: {
            justifyContent: 'center'
        },
        alignCenter: {
            alignItems: 'center'
        },
        root: {
            height: '100%',
            width: '100%',
            padding: 0
        },
        gridHeader: {
            height: headerHeight
        },
        gridTitle: {
            paddingLeft: theme.spacing(1)
        },
        agWrapper: {
            height: `calc(100% - ${headerHeight}px)`
        }
    }
})

function TreeHeader<T extends OcTreeItemBasicProps<T>>(props: TreeHeaderProps<T>) {
    const {model, action, hideHeaderActions, title, expandText, collapseText, searchText} = props
    const {classes, cx} = useStyles()

    const [filterValue, setFilterValue] = React.useState(model.quickFilter)

    const onFilterChange = React.useCallback(
        (newValue: string) => {
            model.setOcQuickFilter(newValue)
        },
        [model]
    )

    const debounceOnChange = React.useMemo(() => debounce(onFilterChange, 500, {leading: true}), [onFilterChange])

    React.useEffect(() => {
        if (model.quickFilter !== OcTreeStateBase.ERROR_SEARCH_VALUE) {
            setFilterValue(model.quickFilter)
        }
    }, [model.quickFilter])

    if (hideHeaderActions) {
        return (
            <Grid container className={cx(classes.gridHeader, classes.displayFlex, classes.alignCenter)}>
                <Grid item xs={8} className={classes.gridTitle}>
                    <Typography>{title}</Typography>
                </Grid>
                <Grid item xs={4} className={cx(classes.displayFlex, classes.justifyEnd)}>
                    {action ? action : undefined}
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid container className={cx(classes.gridHeader, classes.displayFlex, classes.alignCenter)}>
            <Grid item xs={4} className={classes.gridTitle}>
                <Typography>{title}</Typography>
            </Grid>
            <Grid item xs={2} className={cx(classes.displayFlex, classes.justifyCenter)}>
                <OcButton
                    variant="text"
                    onClick={model.collapseAll}
                    data-cy="oc-tree-component-collapse-btn"
                    disabled={model.isQuickFilterActive}
                >
                    {collapseText || 'Collapse'}
                </OcButton>
            </Grid>
            <Grid item xs={2} className={cx(classes.displayFlex, classes.justifyCenter)}>
                <OcButton
                    variant="text"
                    onClick={model.expandAll}
                    data-cy="oc-tree-component-expand-btn"
                    disabled={model.isQuickFilterActive}
                >
                    {expandText || 'Expand'}
                </OcButton>
            </Grid>
            <Grid item xs={3} className={cx(classes.displayFlex, classes.justifyEnd)}>
                <OcTextField
                    variant="outlined"
                    value={filterValue}
                    disabled={model.quickFilter === OcTreeStateBase.ERROR_SEARCH_VALUE}
                    data-cy="oc-tree-component-search"
                    onChange={e => {
                        setFilterValue(e.target.value)
                        debounceOnChange(e.target.value)
                    }}
                    label={searchText || 'Search'}
                    startIcon={<SearchIcon size="24px" />}
                />
            </Grid>
            <Grid item xs={1} className={cx(classes.displayFlex, classes.justifyEnd)}>
                {action ? action : undefined}
            </Grid>
        </Grid>
    )
}

function _OcTree<T extends OcTreeItemBasicProps<T>>(props: OcTreeProps<T>) {
    const {model, action, hideHeaderActions, title, expandText, collapseText, searchText, ...restProps} = props
    const {classes} = useStyles()

    return (
        <Paper square className={classes.root}>
            <TreeHeader
                model={model}
                action={action}
                hideHeaderActions={hideHeaderActions}
                title={title}
                expandText={expandText}
                collapseText={collapseText}
                searchText={searchText}
            />

            <div className={classes.agWrapper}>
                <OcAgGrid
                    suppressMovableColumns={true}
                    headerHeight={0}
                    rowSelection={{
                        mode: 'singleRow',
                        checkboxes: false,
                        enableClickSelection: true
                    }}
                    className={OcTreeClassName}
                    {...restProps}
                />
            </div>
        </Paper>
    )
}

export const OcTree = observer(_OcTree)
